import { useCallback, useEffect, useState } from 'react';
import { getPublicUser, type PublicUserProfile } from '../lib/api';
import { useBackButton } from '../hooks/useBackButton';
import { Skeleton } from '../components/Skeleton';
import { AvatarWithProgress } from '../components/AvatarWithProgress';
import { StreakWidget } from '../components/StreakWidget';
import { BadgesList } from '../components/BadgesList';
import '../components/ProfileScreen.css';

interface UserProfileScreenProps {
  userId: number;
  onBack: () => void;
}

export function UserProfileScreen({ userId, onBack }: UserProfileScreenProps) {
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useBackButton(onBack);

  useEffect(() => {
    setLoading(true);
    getPublicUser(userId)
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleContact = useCallback(() => {
    const username = profile?.username?.replace(/^@/, '').trim();
    if (!username) return;
    const tg = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void } } }).Telegram?.WebApp;
    const url = `https://t.me/${username}`;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [profile?.username]);

  useEffect(() => {
    const mainButton = (window as { Telegram?: { WebApp?: { MainButton?: { hide: () => void; hideProgress?: () => void } } } }).Telegram?.WebApp?.MainButton;
    if (!mainButton) return;
    mainButton.hide();
    mainButton.hideProgress?.();
    return () => {
      mainButton.hide();
      mainButton.hideProgress?.();
    };
  }, []);

  const projectsCount = profile?.projects_count ?? 0;
  const requestsCount = profile?.requests_count ?? 0;
  const streakDays = profile?.streak_days ?? 0;
  const completion = profile?.completion_percent ?? profile?.profile_completeness ?? 0;

  if (loading) {
    return (
      <div className="profile-screen" style={{ padding: 20 }}>
        <Skeleton width={60} height={60} borderRadius={20} />
        <Skeleton width="50%" height={16} style={{ marginTop: 12 }} />
        <Skeleton width="35%" height={12} style={{ marginTop: 6 }} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-screen" style={{ padding: 20 }}>
        <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 40 }}>
          Пользователь не найден
        </p>
      </div>
    );
  }

  return (
    <div className="profile-screen">
      <div className="profile-hero">
        <div className="profile-hero-avatar">
          <AvatarWithProgress avatarUrl={profile.avatar_url} progress={completion} level={1} />
        </div>
        <h2 className="profile-hero-name">{profile.display_name || 'Аноним'}</h2>
        <span className="profile-hero-username">
          {profile.username ? `@${profile.username}` : ''}
        </span>
        {profile.selected_role ? (
          <span className="profile-role-pill">{profile.selected_role}</span>
        ) : null}
      </div>

      <div className="profile-progress-card">
        <div className="profile-progress-header" style={{ marginBottom: 6 }}>
          <span className="profile-progress-label">Профиль</span>
          <span className="profile-progress-value">{Math.round(completion)}%</span>
        </div>
        <div className="profile-progress-bar-track profile-progress-bar">
          <div
            className="profile-progress-bar-fill profile-progress-fill"
            style={{ width: `${Math.max(0, Math.min(100, completion))}%` }}
          />
        </div>
        {profile.next_completion_hint ? (
          <div className="profile-progress-hint">{profile.next_completion_hint}</div>
        ) : null}
      </div>

      {profile.bio && (
        <div className="profile-section">
          <div className="profile-section-header section-label">О себе</div>
          <div className="profile-bio-text">{profile.bio}</div>
        </div>
      )}

      <div className="profile-stats" style={{ marginLeft: 20, marginRight: 20, marginTop: 12 }}>
        <div className="profile-stat">
          <div className="profile-stat-val">{projectsCount}</div>
          <div className="profile-stat-label">Проектов</div>
        </div>
        <div className="profile-stat">
          <div className="profile-stat-val">{requestsCount}</div>
          <div className="profile-stat-label">Заявок</div>
        </div>
        <div className="profile-stat">
          <div className="profile-stat-val">{streakDays}</div>
          <div className="profile-stat-label">Дней подряд</div>
        </div>
      </div>

      <StreakWidget streakDays={streakDays} />
      <BadgesList badges={profile.badges} />

      {profile.username && (
        <div style={{ padding: '0 20px', marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={handleContact}
          >
            Написать
          </button>
        </div>
      )}
    </div>
  );
}
